/**
 * Format DOB string to yyyy-MM-dd format.
 *
 * @param {string|number|Date} dob
 * @returns {string|null}
 */
export const formatDob = (dob) => {
  if (!dob) return null;
  try {
    // If it is already a yyyy-MM-dd string, return as-is
    if (typeof dob === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      return dob;
    }
    const date = new Date(dob);
    if (isNaN(date.getTime())) return null;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.error("Failed to format DOB", e);
    return null;
  }
};

/**
 * Basic user payload — matches legacy $scope.userDetails shape exactly.
 *
 * @param {object} formData
 * @param {boolean} isCreate
 * @returns {object}
 */
export const buildBasicPayload = (formData, isCreate) => {
  const brandId = window?.LTD?.getSiteInfo?.()?.brandId || '1672';
  const payload = {
    brandId: formData.type === 'USER' ? 0 : Number(brandId),
    fullName: formData.name.trim(),
    primaryEmail: formData.email.trim(),
    countryCode: formData.countryCode,
    primaryMobile: formData.mobile.trim(),
    gender: formData.gender || 'NOT_SHARED',
    dob: formatDob(formData.dob),
    source: formData.source || '0',
    type: formData.type || 'USER',
    status: formData.status || 'ACTIVE',
  };
  if (isCreate) {
    payload.password = formData.password;
  }
  return payload;
};

/**
 * Brand access payload — matches legacy saveBrandAccess shape.
 * { userBrandOutletAccesses: { [brandId]: [outletId, ...] } }
 *
 * @param {object} formData
 * @returns {object|null}
 */
export const buildBrandAccessPayload = (formData) => {
  const outlets = formData.brandAccessOutlets;
  if (!outlets || Object.keys(outlets).length === 0) return null;
  return { userBrandOutletAccesses: outlets };
};

/**
 * Role payload — matches legacy assignRoleGroup shape.
 * { permissions: { accesses: [{ roleGroupId }] } }
 *
 * @param {object} formData
 * @returns {object|null}
 */
export const buildRolePayload = (formData) => {
  if (!formData.selectedRole) return null;
  return {
    permissions: {
      accesses: [{ roleGroupId: formData.selectedRole }],
    },
  };
};

/**
 * Permissions payload — matches legacy assignPermissions shape.
 * { permissions: { accesses: [{ roleId, brandOutletExclusions: {} }] } }
 *
 * @param {object} formData
 * @param {Array} initialPermissionRoleIds
 * @returns {object|null}
 */
export const buildPermissionsPayload = (formData, initialPermissionRoleIds = []) => {
  const selectedIds = formData.selectedPermissionRoleIds || [];
  const initialIds = initialPermissionRoleIds || [];

  const removedPermissionRoleIds = initialIds
    .map(String)
    .filter((id) => !selectedIds.map(String).includes(id));

  if (selectedIds.length === 0 && removedPermissionRoleIds.length === 0) return null;

  return {
    permissions: {
      accesses: selectedIds.map((roleId) => ({
        roleId: String(roleId),
        brandOutletExclusions: {},
      })),
    },
    removedPermissionRoleIds,
  };
};

